import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  BackAndMultipleButtons,
  SaveButton,
  SaveTempButton,
} from 'components/base/BottomStepComponent';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import {
  APIStatus,
  ActionType,
  ApprovalStatus,
  CampaignType,
} from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { validate } from 'containers/engagement/campaignStreamline/CreateCampaignValidate';

import { scrollToFirstError } from './CreateCampaignHandleError';
import PublishCampaignPrompt from '../campaign/campaignCreation/PublishCampaignPrompt';
import RejectPrompt from '../campaign/campaignCreation/RejectPrompt';

import { createAction } from 'utils';

import BaseCreationStepContentByConfig from 'components/base/BaseCreationStepContentByConfig';
import { CREATE_CAMPAIGN_PREVIEW_PAGE_CONFIG } from 'containers/engagement/campaignStreamline/CreateCampaignPerviewPageConfig';
import RecurringMessageSendToAllAgainPrompt from '../campaign/campaignCreation/RecurringMessageSendToAllAgainPrompt';
import './CreateCampaignPreviewPage.scss';
import RequireCampaignNamePrompt from './RequireCampaignNamePrompt';
import AnchorPage from './AnchorPage';

const CreateCampaignPreviewPage = ({
  currentStep,
  createStatus,
  formHasSubmitted,
  listpath = "/campaigns",
  isSuperuser,
  userPermissions,
  campaignId,
  stepWithCoupon,
  previewPageConfig = CREATE_CAMPAIGN_PREVIEW_PAGE_CONFIG,
  isMessageCampaign,
}) => {
  const params = useParams();
  const { watch, getValues, setValue, clearErrors, setError, formState } = useFormContext();
  const errors = formState.errors;
  const location = useLocation();

  const watchName = watch('translations.en.generalName');
  const [showRequireNamePrompt, setShowRequireNamePrompt] = useState(false);
  const [showPublishPrompt, setShowPublishPrompt] = useState(false);
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [showSendToAllPrompt, setShowSendToAllPrompt] = useState(false);
  const [actionType, setActionType] = useState(ActionType.none);
  const approvalStatus = getValues('approvalStatus');
  const campaignType = getValues('campaignType');
  const isBirthdayRecurringCampaign = getValues('isBirthdayRecurringCampaign');
  const recurringMessageChannels = getValues('recurringMessage.channels');

  const dispatch = useDispatch();
  const history = useHistory();
  const hasPermission = (requires) => {
    if (isSuperuser) return true;
    if (userPermissions.includes(requires) || !requires) return true;

    return false;
  };

  const hasPublishPermisson = hasPermission(isMessageCampaign ? PermissionCodes.publishPendingMessage: PermissionCodes.publishPendingCampaign);
  const showForceSaveButton = hasPublishPermisson && ![ApprovalStatus.pending, ApprovalStatus.published].includes(approvalStatus);

  useEffect(() => {
    scrollToFirstError(errors, []); // twitch TODO
  }, [errors]);

  useEffect(() => {
    if (createStatus === APIStatus.success && formHasSubmitted) {
      setShowPublishPrompt(
        actionType !== ActionType.approval ||
        history.location.pathname.includes('edit'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createStatus, formHasSubmitted]);

  const stepChangeAction = (step, buttonAction = null, checkCheckList = false) => {
    if (step || step === 0) {
      dispatch({
        type: 'campaignStreamline/stepChange',
        payload: { step: step },
      });
      return;
    };

    let isValid = true;

    if (
      [
        ActionType.update,
        ActionType.save,
        ActionType.tempForPulished,
      ].includes(buttonAction)
    ) {
      if (!watchName) {
        setActionType(buttonAction);
        setShowRequireNamePrompt(true);
        return;
      };
    } else {
      isValid = validate({
        stepWithCoupon,
        dispatch,
        getValues: getValues,
        setError,
        clearErrors,
        checkCheckList,
      });
    }

    if (isValid) {
      setActionType(buttonAction);
      if (
        buttonAction === ActionType.withdraw ||
        buttonAction === ActionType.unpublish
      ) {
        dispatch(
          createAction('createCampaign/updateApprovalStatus')({
            data: getValues(),
            actionType: buttonAction,
            deleteBanner: buttonAction === ActionType.unpublish,
          }),
        );
      } else {
        // show recurring message send to all prompt
        if (
          isBirthdayRecurringCampaign &&
          [ActionType.forcePublish, ActionType.approval].includes(buttonAction) &&
          recurringMessageChannels &&
          recurringMessageChannels?.length > 0
        ) {
          setShowSendToAllPrompt(true);
          return;
        }
        if (
          buttonAction === ActionType.publish ||
          buttonAction === ActionType.forcePublish ||
          buttonAction === ActionType.approval
        ) {
          if (!params.id) {
            dispatch(
              createAction('createCampaign/prePublishCampaign')({
                data: getValues(),
                resume: location.state?.resume,
              }),
            );
            return;
          };

          dispatch(
            createAction('createCampaign/publishCampaignV2')({
              data: getValues(),
              resume: location.state?.resume,
            }),
          );
          return;
        }
        dispatch(
          createAction('createCampaign/createOrUpdateCampaignV2')({
            data: getValues(),
            actionType: buttonAction,
            resume: location.state?.resume,
          }),
        );
      }
    }
  };

  const approvalButtonAction = () => {
    stepChangeAction(null, approvalActionType, true);
  };

  const forcePublishButtonAction = () => {
    stepChangeAction(null, ActionType.forcePublish, true);
  };

  const tempSaveButtonMap = {
    [ApprovalStatus.draft]: {
      text: 'Update',
      actionType: ActionType.update,
      requirePermission: isMessageCampaign ? PermissionCodes.editDraftMessage : PermissionCodes.editDraftCampaign,
    },
    [ApprovalStatus.pending]: hasPublishPermisson ? {
      text: 'Update',
      actionType: ActionType.update,
      requirePermission: isMessageCampaign ? PermissionCodes.publishPendingMessage : PermissionCodes.publishPendingCampaign,
    } : {
      text: 'Withdraw',
      actionType: ActionType.withdraw,
      requirePermission: isMessageCampaign ? PermissionCodes.withdrawPendingMessage : PermissionCodes.withdrawPendingCampaignToDraft,
    },
    [ApprovalStatus.published]: {
      text: 'Update',
      actionType: ActionType.tempForPulished,
      requirePermission: isMessageCampaign ? PermissionCodes.createTempMessageFromPublish : PermissionCodes.tempForPublishCampaign,
    },
    default: {
      text: 'Save as a draft',
      actionType: ActionType.save,
      requirePermission: isMessageCampaign ? PermissionCodes.createDraftMessage : PermissionCodes.createDraftCampaign,
    },
  }

  const [tempButtonText, tempActionType, tempRequires] = useMemo(() => {
    const tempActionConfig = tempSaveButtonMap?.[approvalStatus] || tempSaveButtonMap?.default

    return [
      tempActionConfig?.text,
      tempActionConfig?.actionType,
      tempActionConfig?.requirePermission,
    ]
  }, [approvalStatus, tempSaveButtonMap]);


  const approvalActionMap = {
    [ApprovalStatus.published]: {
      text: 'Unpublish',
      actionType: ActionType.unpublish,
      requirePermission: isMessageCampaign
        ? hasPermission(PermissionCodes.unPublishMessageToPending)
          ? PermissionCodes.unPublishMessageToPending : PermissionCodes.unPublishMessageToDraft
        : hasPermission(PermissionCodes.withdrawPublishCampaignToPending)
          ? PermissionCodes.withdrawPublishCampaignToPending : PermissionCodes.withdrawPublishCampaignToDraft
      },
    [ApprovalStatus.pending]: hasPublishPermisson ? {
      text: 'Approve',
      actionType: ActionType.approval,
      requirePermission: isMessageCampaign ? PermissionCodes.publishPendingMessage : PermissionCodes.publishPendingCampaign,
    } : {
      text: 'Pending for approval',
      actionType: ActionType.pendingForApproval,
      requirePermission: isMessageCampaign ? PermissionCodes.withdrawPendingMessage : PermissionCodes.withdrawPendingCampaignToDraft,
    },
    default: {
      text: 'Submit for approval',
      actionType: ActionType.submitForApproval,
      requirePermission: isMessageCampaign ? PermissionCodes.pendingDraftMessage : PermissionCodes.pendingCampaign,
    },
  }

  const [approvalText, approvalActionType, saveRequires] = useMemo(() => {
    const approvalActionConfig = approvalActionMap?.[approvalStatus] || approvalActionMap?.default

    return [approvalActionConfig?.text, approvalActionConfig?.actionType, approvalActionConfig?.requirePermission]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus, hasPublishPermisson, approvalActionMap])

  const extraPopContent = useMemo(() => {
    const extraPopContentList = [
      {
        content: tempButtonText,
        action: () => stepChangeAction(null, tempActionType, false),
      },
    ];

    if (approvalStatus === ApprovalStatus.pending) {
      extraPopContentList.push({
        content: 'Reject',
        action: () => {
          setShowRejectPrompt(true);
        },
        requires: isMessageCampaign ? PermissionCodes.publishPendingMessage : PermissionCodes.publishPendingCampaign,
      });
    }
    return extraPopContentList;
  }, [approvalStatus]);

  const extraButtons = useMemo(() => {
    const buttonList = [];
    if (hasPermission(tempRequires)) {
      buttonList.push(
        <SaveTempButton
          text={tempButtonText}
          action={() => stepChangeAction(null, tempActionType, false)}
        />,
      );
    }
    if (
      approvalStatus === ApprovalStatus.pending &&
      hasPublishPermisson
    ) {
      buttonList.push(
        <SaveTempButton
          text="Reject"
          action={() => {
            setShowRejectPrompt(true);
          }}
        />,
      );
    }
    if (showForceSaveButton && hasPermission(saveRequires)) {
      buttonList.push(
        <SaveTempButton
          text={approvalText}
          action={() => approvalButtonAction()}
          disabled={
            approvalActionType === ActionType.pendingForApproval ? true : false
          }
        />,
      );
    }
    return buttonList;
  }, [approvalStatus]);

  const primaryButton = useMemo(() => {
    if (showForceSaveButton) {
      return (
        <SaveButton
          text={'Skip approval and Publish'}
          action={() => forcePublishButtonAction()}
        />
      );
    }
    if (hasPermission(saveRequires)) {
      return (
        <SaveButton
          text={approvalText}
          action={() => approvalButtonAction()}
          disabled={
            approvalActionType === ActionType.pendingForApproval ? true : false
          }
        />
      );
    }
    return null;
  }, [approvalStatus]);

  return (
    <div className='create-campaign-preview-page'>
      <div className='create-campaign-preview-page-with-anchor'>
        <BaseCreationStepContentByConfig
          configuration={previewPageConfig || CREATE_CAMPAIGN_PREVIEW_PAGE_CONFIG}
          hidePreview={true}
        />
        <AnchorPage />
      </div>

      <BackAndMultipleButtons
        backAction={() => stepChangeAction(currentStep - 1, null, false)}
        multipleButtons={[
          <ListButtonsGroup
            hideExtraButtonWidth={600}
            hideAllButtonWidth={400}
            hideExtraPopPosition="top"
            hideAllPopPosition="topRight"
            extraButtons={extraButtons}
            extraPopContent={extraPopContent}
            primaryButton={primaryButton}
            primaryPopContent={{
              requires: saveRequires,
              action: () => approvalButtonAction(),
              content: approvalText,
            }}
          />,
        ]}
      />

      <RequireCampaignNamePrompt
        show={showRequireNamePrompt}
        onHide={() => {
          setActionType(ActionType.none);
          setShowRequireNamePrompt(false);
        }}
        onConfirm={() => {
          setShowRequireNamePrompt(false);
          dispatch(
            createAction('createCampaign/createOrUpdateCampaignV2')({
              data: getValues(),
              actionType: actionType,
              resume: location.state?.resume,
            }),
          );
        }}
      />

      <RejectPrompt
        show={showRejectPrompt}
        onHide={() => setShowRejectPrompt(false)}
        onConfirm={(comment) => {
          setShowRejectPrompt(false);
          dispatch(
            createAction('createCampaign/rejectCampaign')({
              id: params.id,
              message: comment,
              afterAction: () => {
                const pathname = location.pathname.split('/')[1];
                history.push({
                  pathname: `/${pathname}`,
                });
              },
            }),
          );
        }}
      />

      <RecurringMessageSendToAllAgainPrompt
        show={showSendToAllPrompt}
        onHide={() => setShowSendToAllPrompt(false)}
        onConfirm={(sendToAllAgain) => {
          setShowSendToAllPrompt(false);
          setValue(`recurringMessage.sendToAllAgain`, sendToAllAgain, { shouldDirty: true })
          if (
            actionType === ActionType.publish ||
            actionType === ActionType.forcePublish ||
            actionType === ActionType.approval
          ) {
            if (!params.id) {
              dispatch(
                createAction('createCampaign/prePublishCampaign')({
                  data: getValues(),
                  resume: location.state?.resume,
                }),
              );
              return;
            };

            dispatch(
              createAction('createCampaign/publishCampaignV2')({
                data: getValues(),
                resume: location.state?.resume,
              }),
            );
            return;
          }
          dispatch(
            createAction('createCampaign/createOrUpdateCampaignV2')({
              data: getValues(),
              actionType: actionType,
              resume: location.state?.resume,
            }),
          );
        }}
      />

      <PublishCampaignPrompt
        campaignId={campaignId}
        campaignType={isMessageCampaign ? CampaignType.generalMessageCampaign : campaignType}
        actionType={actionType}
        show={showPublishPrompt}
        path={listpath}
      />
    </div>
  );
}

const mapPropsToState = (state) => ({
  stepWithCoupon: state.campaignStreamline.stepWithCoupon,
  currentStep: state.campaignStreamline.currentStep,
  createStatus: state.createCampaign.createStatus,
  formHasSubmitted: state.createCampaign.formHasSubmitted,
  isSuperuser: state.users.isSuperuser,
  userPermissions: state.users.userPermissions,
  campaignId: state.createCampaign.campaign?.pk,
});

export default connect(mapPropsToState)(CreateCampaignPreviewPage);
